import { emptyMods, type ModFlags, type ModKey, type ModPayload } from "../../../shared/mods";

export const ADMIN_NAME = "Daniel Guzev";
const PASSCODE = "2319";
const API_BASE = "/api";

export class Mods {
  noclip = false;
  startingScore = 0;
  startingFlies = 0;
  /** Per-session: must enter the passcode once before ModScene opens. */
  unlocked = false;

  /**
   * When true, this player manages their own mods locally via the mod menu (an
   * admin/owner). The self-poll is skipped for them so it can't clobber a
   * locally-set noclip/flag — the poll only exists to deliver admin-pushed
   * pranks to victims, who are never admins.
   */
  selfManaged = false;

  /** Pranks pushed to this client's player (by an admin, or self). Applied live in PlayScene. */
  flags: ModFlags = emptyMods();

  /**
   * Set by the self-poll when an admin has flagged this player to pick a new
   * name. Game watches this and runs the forced-rename flow; it clears once the
   * rename completes (the server clears the flag too).
   */
  forceRenameRequested = false;

  /**
   * Set by the self-poll when an admin has banned this player. Game watches this
   * and locks the client into the BanScene (and the server rejects their login
   * and run submissions). Clears if the ban is lifted server-side.
   */
  banned = false;

  isAdmin(playerName: string): boolean {
    return playerName === ADMIN_NAME;
  }

  tryUnlock(code: string): boolean {
    if (code === PASSCODE) {
      this.unlocked = true;
      return true;
    }
    return false;
  }

  lock(): void {
    this.unlocked = false;
  }

  ensureUnlocked(): boolean {
    if (this.unlocked) return true;
    const code = window.prompt("Enter passcode:") ?? "";
    if (code === "") return false;
    if (this.tryUnlock(code)) return true;
    window.alert("Incorrect passcode.");
    return false;
  }

  consumeStartingScore(): number {
    const v = this.startingScore;
    this.startingScore = 0;
    return v;
  }

  consumeStartingFlies(): number {
    const v = this.startingFlies;
    this.startingFlies = 0;
    return v;
  }

  setFlag(key: ModKey, value: boolean): void {
    this.flags[key] = value;
  }

  setFlags(next: ModFlags): void {
    this.flags = { ...next };
  }

  /**
   * Poll our own pending mods from the server. Called periodically while logged
   * in. Applies both grief flags and the legacy slice (noclip + injected
   * starting score/flies) an admin may have pushed to us.
   */
  async pollSelf(name: string): Promise<void> {
    if (!name || this.selfManaged) return;
    try {
      const res = await fetch(`${API_BASE}/mods/${encodeURIComponent(name)}`);
      if (!res.ok) return;
      const data = (await res.json()) as {
        mods?: ModPayload | null;
        forceRename?: boolean;
        banned?: boolean;
      };
      // Set these before the no-mods early return — a flagged/banned player may
      // have no pending pranks at all, but still needs to be acted on.
      this.forceRenameRequested = Boolean(data.forceRename);
      this.banned = Boolean(data.banned);
      if (!data.mods) return;
      this.setFlags(data.mods.flags);
      const lg = data.mods.legacy;
      this.noclip = lg.noclip;
      // Re-arm the injected starters every poll so each run the victim starts
      // gets them, until the admin clears the push. consume*() zeroes them per
      // run; this puts them back.
      this.startingScore = lg.startingScore;
      this.startingFlies = lg.startingFlies;
    } catch {
      // ignore
    }
  }

  /** Fetch a target's current pending mods (used by the admin ModScene to seed its UI). */
  static async fetchTarget(name: string): Promise<ModPayload | null> {
    try {
      const res = await fetch(`${API_BASE}/mods/${encodeURIComponent(name)}`);
      if (!res.ok) return null;
      const data = (await res.json()) as { mods?: ModPayload | null };
      return data.mods ?? null;
    } catch {
      return null;
    }
  }

  /** Push mods to a target. Admin-only on the server. */
  static async pushTo(
    adminName: string,
    target: string,
    mods: ModPayload,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/set-mods`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target, mods }),
      });
      if (res.ok) return { ok: true };
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /** Grant or revoke a single item for a target. Used for restricted items. */
  static async setItemOwned(
    adminName: string,
    target: string,
    itemId: string,
    owned: boolean,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/set-item-owned`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target, itemId, owned }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) return { ok: true };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /**
   * Grant or revoke a LIST of items for a target in one request. Use this for
   * whole collections — firing many setItemOwned calls concurrently races on
   * the server (lost writes) so only some items would actually stick.
   */
  static async grantItems(
    adminName: string,
    target: string,
    itemIds: readonly string[],
    owned: boolean,
  ): Promise<{ ok: boolean; count?: number; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/grant-items`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target, itemIds, owned }),
      });
      const data = (await res.json().catch(() => ({}))) as { count?: number; error?: string };
      if (res.ok) return { ok: true, count: data.count ?? 0 };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /** Owner-only: grant or revoke a player's mod-menu (admin) access. */
  static async setAdmin(
    ownerName: string,
    target: string,
    admin: boolean,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/set-admin`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: ownerName, target, admin }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      return res.ok ? { ok: true } : { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /** Owner-only: revoke mod-menu access from everyone except the owner. */
  static async revokeAdmins(
    ownerName: string,
  ): Promise<{ ok: boolean; revoked?: number; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/revoke-admins`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: ownerName }),
      });
      const data = (await res.json().catch(() => ({}))) as { revoked?: number; error?: string };
      return res.ok ? { ok: true, revoked: data.revoked ?? 0 } : { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /** Owner-only: claw back every admin-granted item from everyone except the owner. */
  static async revokeGrants(
    ownerName: string,
  ): Promise<{ ok: boolean; revoked?: number; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/revoke-grants`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: ownerName }),
      });
      const data = (await res.json().catch(() => ({}))) as { revoked?: number; error?: string };
      return res.ok ? { ok: true, revoked: data.revoked ?? 0 } : { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /**
   * Reset a target's password (admin-only on the server). The admin supplies a
   * new password; the old one is never revealed (it isn't stored in plaintext).
   * The target's existing session is invalidated server-side.
   */
  static async resetPassword(
    adminName: string,
    target: string,
    newPassword: string,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/reset-password`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target, password: newPassword }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) return { ok: true };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /**
   * Flag (or clear) a target so they're forced to pick a new name (admin-only on
   * the server). The victim is re-prompted via their self-poll until they pick a
   * new name; their progress is migrated by the rename, which clears the flag.
   */
  static async forceRename(
    adminName: string,
    target: string,
    force = true,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/force-rename`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target, force }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) return { ok: true };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /**
   * Ban (or unban) a target (admin-only on the server). A banned account can't
   * log in or submit runs, is hidden from the leaderboard, and any open session
   * is locked out via its self-poll. Pass `banned = false` to lift the ban.
   */
  static async setBan(
    adminName: string,
    target: string,
    banned: boolean,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/ban`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target, banned }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) return { ok: true };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  /** Overwrite a target's owned_items list (admin). Empty list = lock all. */
  static async setOwned(
    adminName: string,
    target: string,
    items: string[],
  ): Promise<{ ok: boolean; count?: number; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/admin/set-owned`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target, items }),
      });
      const data = (await res.json().catch(() => ({}))) as { count?: number; error?: string };
      if (res.ok) return { ok: true, count: data.count ?? 0 };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }
}
